import React from "react";
import { motion } from "framer-motion";
import { Phone, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";

const WHATSAPP_LINK = "https://wa.me/919866679191?text=Hi%2C%20I%27m%20interested%20in%20your%20paint%20services";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_sri-paint-studio/artifacts/ab228uec_ChatGPT%20Image%20Feb%203%2C%202026%2C%2004_43_55%20PM.png";

export const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-screen bg-white overflow-hidden" data-testid="hero-section">
      {/* Light background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-purple-50" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-24 sm:pt-28 lg:pt-32 pb-12 lg:pb-20">
        {/* Mobile: Image first, then content */}
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          
          {/* Family Image - Shows on ALL devices */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8 }}
            className="w-full order-1 lg:order-2"
          >
            <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl mx-auto max-w-md lg:max-w-none">
              <img 
                src={HERO_IMAGE} 
                alt="Happy Family Painting House - Sri Pavanasutha Traders" 
                className="w-full h-auto object-cover"
              />
            </div>
          </motion.div>

          {/* Content */}
          <div className="space-y-5 sm:space-y-6 order-2 lg:order-1 text-center lg:text-left">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-100 text-purple-700 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                Since 2017 • Godavarikhani
              </span>
              
              {/* Shop Name - Large & Prominent */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-2">
                <span className="text-purple-600">Sri Pavanasutha</span>
                <span className="block text-2xl sm:text-3xl lg:text-4xl text-purple-600 mt-1">Traders</span>
              </h1>
              
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mt-3">
                The Whole Paint Gallery
              </h2>
            </motion.div>

            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.15 }} 
              className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-lg mx-auto lg:mx-0"
            >
              Transform your spaces with premium paints, textures, and wall finishes. 
              Authorized dealer of Asian Paints, Nexon Paints & Birla Opus.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.3 }} 
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start"
            >
              <Button 
                asChild 
                size="lg" 
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 sm:px-8 py-5 sm:py-6 rounded-full text-base sm:text-lg font-semibold shadow-lg w-full sm:w-auto" 
                data-testid="hero-cta-button"
              >
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                  <Phone className="w-5 h-5 mr-2" />
                  Get Free Consultation
                </a>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="border-2 border-gray-300 hover:border-purple-500 hover:bg-purple-50 px-6 sm:px-8 py-5 sm:py-6 rounded-full text-base sm:text-lg font-medium w-full sm:w-auto" 
                data-testid="hero-explore-button"
              >
                <a href="#products">
                  Explore Products
                  <ChevronDown className="w-5 h-5 ml-2" />
                </a>
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.45 }} 
              className="flex justify-center lg:justify-start gap-6 sm:gap-8 pt-4 sm:pt-6"
            >
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">8+</p>
                <p className="text-xs sm:text-sm text-gray-500">Years Experience</p>
              </div>
              <div className="w-px bg-gray-200" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">3</p>
                <p className="text-xs sm:text-sm text-gray-500">Premium Brands</p>
              </div>
              <div className="w-px bg-gray-200" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">1000+</p>
                <p className="text-xs sm:text-sm text-gray-500">Happy Customers</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
